import { supabase } from "@/integrations/supabase/client";
import { DbSuggestedTask, SuggestedTask, mapDbTaskToUiTask } from "./suggested-task-types";

export async function fetchSuggestedTasks(goalId: string, userId: string): Promise<SuggestedTask[]> {
  // Note: RLS policies handle access control (owned + collaborative access)
  const { data, error } = await supabase
    .from("planner_suggested_tasks")
    .select("*")
    .eq("goal_id", goalId)
    .eq("status", "pending")
    .order("display_order", { ascending: false })
    .order("created_at", { ascending: false });
  if (error) throw error;
  return (data as DbSuggestedTask[]).map(mapDbTaskToUiTask);
}

export async function createSuggestedTask(task: Omit<SuggestedTask, "id" | "createdAt" | "updatedAt">): Promise<SuggestedTask> {
  const { goalId, userId, title, description, status } = task;
  
  const { data: existingTasks } = await supabase
    .from("planner_suggested_tasks")
    .select("display_order")
    .eq("goal_id", goalId)
    .eq("user_id", userId)
    .eq("status", "pending")
    .order("display_order", { ascending: false })
    .limit(1);
  
  const maxDisplayOrder = existingTasks?.[0]?.display_order || 0;
  const newDisplayOrder = maxDisplayOrder + 1;
  
  const toInsert = {
    goal_id: goalId,
    user_id: userId,
    title,
    description: description ?? null,
    status,
    display_order: newDisplayOrder,
  };
  const { data, error } = await supabase
    .from("planner_suggested_tasks")
    .insert([toInsert])
    .select()
    .single();
  if (error) throw error;
  return mapDbTaskToUiTask(data as DbSuggestedTask);
}

export async function updateSuggestedTaskStatus(
  taskId: string,
  status: "approved" | "rejected",
  userId: string
): Promise<SuggestedTask> {
  const { data, error } = await supabase
    .from("planner_suggested_tasks")
    .update({ status })
    .eq("id", taskId)
    .eq("user_id", userId)
    .select()
    .single();
  if (error) throw error;
  return mapDbTaskToUiTask(data as DbSuggestedTask);
}

export async function deleteSuggestedTask(
  taskId: string,
  userId: string
): Promise<void> {
  const { error } = await supabase
    .from("planner_suggested_tasks")
    .delete()
    .eq("id", taskId)
    .eq("user_id", userId);
  if (error) throw error;
}

export async function countPendingSuggestedTasks(goalId: string, userId: string): Promise<number> {
  // Note: RLS policies handle access control (owned + collaborative access)
  const { count, error } = await supabase
    .from("planner_suggested_tasks")
    .select("*", { count: "exact", head: true })
    .eq("goal_id", goalId)
    .eq("status", "pending");
  if (error) throw error;
  return count ?? 0;
}
